/**
 * VerifyCodeScreen - Enter 6-digit verification code
 *
 * Simple wireframe UI - easy to replace later
 * User enters code received via email
 * Manages two-modal welcome flow for first-time logins
 *
 * Handles two flows:
 * - Normal user → redirect to /welcome
 * - Applicant (email verified, no profile) → redirect to /apply
 */

import { SUPPORT_EMAIL } from '@/config/brandConfig'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth'

interface VerifyCodeScreenProps {
  email: string
  onBack: () => void
}

export function VerifyCodeScreen({ email, onBack }: VerifyCodeScreenProps) {
  const navigate = useNavigate()
  const { login, error, clearError, isLoading } = useAuth()
  const [code, setCode] = useState('')
  const [localError, setLocalError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError('')
    clearError()

    // Validate code
    if (!code || code.length !== 6) {
      setLocalError('Please enter the 6-digit code')
      return
    }

    try {
      const result = await login(email, code)

      // ======================================================================
      // APPLICANT FLOW: Email verified but no profile
      // Redirect to /apply where they'll complete their application
      // ======================================================================
      if (result.isApplicant) {
        navigate('/apply')
        return
      }

      // ======================================================================
      // NORMAL USER FLOW: Profile exists
      // Check if user came from /vip and redirect back there
      // ======================================================================
      const params = new URLSearchParams(window.location.search)
      const returnTo = params.get('returnTo')
      if (returnTo === '/vip') {
        navigate('/vip', { replace: true })
        return
      }
      navigate('/welcome')
    } catch (err) {
      // Error is already set in context
      console.error('[VerifyCodeScreen] ❌ Failed to verify code:', err)
    }
  }

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setCode(value)
  }

  return (
    <div className="min-h-screen flex items-center justify-center starry-background px-4">
        <div className="max-w-md w-full space-y-8 bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8">
        {/* Header */}
        <div className="text-center animate-slide-up">
          <h1 className="text-3xl font-bold text-[#0E4B66]">
            Enter verification code
          </h1>
          <p className="mt-2 text-[#1D6180]">
            We sent a 6-digit code to
          </p>
          <p className="font-medium text-[#0E4B66]">
            {email}
          </p>
        </div>

        {/* Verify Form */}
        <form onSubmit={handleSubmit} className="mt-8 space-y-6 animate-slide-up animate-delay-100">
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-700">
              Verification code
            </label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              value={code}
              onChange={handleCodeChange}
              placeholder="000000"
              maxLength={6}
              disabled={isLoading}
              className="mt-1 block w-full px-3 py-3 text-center text-2xl tracking-widest border border-gray-300 rounded-md shadow-sm input-focus-ring focus:outline-none focus:ring-[#4AA3CC] focus:border-[#4AA3CC]"
            />
          </div>

          {/* Error messages */}
          {(localError || error) && (
            <div className="text-red-600 text-sm text-center animate-fade-in">
              {localError || error}
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={isLoading || code.length !== 6}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#4AA3CC] hover:bg-[#3B8DB3] btn-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#94d5ee] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Verifying...' : 'Verify code'}
          </button>

          {/* Back button */}
          <button
            type="button"
            onClick={onBack}
            disabled={isLoading}
            className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white btn-scale focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#94d5ee] disabled:opacity-50"
          >
            Use different email
          </button>
        </form>

        {/* Helper text */}
        <p className="mt-4 text-center text-sm text-gray-500">
          Didn't receive the code? Check your spam folder or try again
        </p>

        {/* Support button */}
        <div className="text-center mt-4">
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-full transition-all"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Support
          </a>
        </div>
      </div>
    </div>
  )
}
